import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AgxSpacer,
  CampaignPresentationFields,
  MediaLinksValidator,
  type MediaLinkValidationErrors,
} from '@urbanx/agx.ui-components2';
import Card from 'components/card/Card';
import { AgxToast } from '@urbanx/agx-ui-components';
import {
  CampaignDetailModel,
  StaffGetPhotosAndMediaDetailsForCampaignModel,
  UploadedDocument,
  UploadedDocumentDocumentType,
  useInvalidateUseStaffGetPhotosAndMediaDetailsForCampaign,
  useStaffGetPhotosAndMediaDetailsForCampaign,
  useStaffUpdatePhotosAndMediaDetailsForCampaign,
} from '@urbanx/agx.campaigns.client';
import { AgxToastState } from 'types/commonTypes';
import { CampaignImageUploader } from '@urbanx/agx.ui-components2';

interface PhotosAndMediaProps {
  campaign?: CampaignDetailModel;
  onSaveStateChange: (state: {
    saveChanges: (() => void) | undefined;
    isFormDirty: boolean;
    isFormValid: boolean;
    isPending: boolean;
    showSavedMessage: boolean;
  }) => void;
}

const PhotosAndMedia = ({ campaign, onSaveStateChange }: PhotosAndMediaProps) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [photosAndMediaDetails, setPhotosAndMediaDetails] =
    useState<StaffGetPhotosAndMediaDetailsForCampaignModel | null>(null);
  const [isPhotosBusy, setIsPhotosBusy] = useState(false);
  const [isFloorPlansBusy, setIsFloorPlansBusy] = useState(false);
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const [validationErrors, setValidationErrors] = useState<MediaLinkValidationErrors>({});

  const initialLoadDoneRef = useRef(false);

  const invalidateStaffGetPhotosAndMediaDetails =
    useInvalidateUseStaffGetPhotosAndMediaDetailsForCampaign();

  const { data: campaignPhotosAndMediaDetails } = useStaffGetPhotosAndMediaDetailsForCampaign(
    campaign?.campaignId || '',
    !!campaign?.campaignId
  );

  const {
    staffUpdatePhotosAndMediaDetailsForCampaign,
    staffUpdatePhotosAndMediaDetailsForCampaignPending,
  } = useStaffUpdatePhotosAndMediaDetailsForCampaign({
    onSuccess: async () => {
      await invalidateStaffGetPhotosAndMediaDetails(campaign?.campaignId || '');
      initialLoadDoneRef.current = false;
      setIsFormDirty(false);
      setShowSavedMessage(true);

      updateToastState({
        color: 'success',
        message: 'Listing details updated successfully',
        open: true,
      });
    },
    onError: (error) => {
      console.error('Error updating listing details:', error);
      updateToastState({
        color: 'error',
        message: 'Error updating listing details',
        open: true,
      });
    },
  });

  useEffect(() => {
    if (campaignPhotosAndMediaDetails && !initialLoadDoneRef.current) {
      setPhotosAndMediaDetails({
        ...campaignPhotosAndMediaDetails,
        photos: campaignPhotosAndMediaDetails.photos?.map((d) => ({ ...d })) || [],
        floorPlans: campaignPhotosAndMediaDetails.floorPlans?.map((d) => ({ ...d })) || [],
        listingDescription: {
          ...campaignPhotosAndMediaDetails.listingDescription,
        },
        mediaLinks: { ...campaignPhotosAndMediaDetails.mediaLinks },
      });
      setIsFormDirty(false);
      setValidationErrors({});
      initialLoadDoneRef.current = true;
    }
  }, [campaignPhotosAndMediaDetails]);

  // Check if form is dirty
  useEffect(() => {
    if (!initialLoadDoneRef.current || !photosAndMediaDetails || !campaignPhotosAndMediaDetails)
      return;

    const hasImageOrderChanged = (
      updatedImages: UploadedDocument[] | undefined,
      existingImages: UploadedDocument[] | undefined
    ) => {
      const getIds = (images: UploadedDocument[] | undefined) =>
        (images || []).map((d) => d.documentId);
      return JSON.stringify(getIds(updatedImages)) !== JSON.stringify(getIds(existingImages));
    };

    const dirty =
      photosAndMediaDetails.listingDescription?.headline !==
        campaignPhotosAndMediaDetails.listingDescription?.headline ||
      photosAndMediaDetails.listingDescription?.description !==
        campaignPhotosAndMediaDetails.listingDescription?.description ||
      photosAndMediaDetails.mediaLinks?.videoLink !==
        campaignPhotosAndMediaDetails.mediaLinks?.videoLink ||
      photosAndMediaDetails.mediaLinks?.virtualTourLink !==
        campaignPhotosAndMediaDetails.mediaLinks?.virtualTourLink ||
      photosAndMediaDetails.mediaLinks?.externalLink !==
        campaignPhotosAndMediaDetails.mediaLinks?.externalLink ||
      hasImageOrderChanged(photosAndMediaDetails.photos, campaignPhotosAndMediaDetails.photos) ||
      hasImageOrderChanged(
        photosAndMediaDetails.floorPlans,
        campaignPhotosAndMediaDetails.floorPlans
      );

    setIsFormDirty(dirty);
  }, [photosAndMediaDetails, campaignPhotosAndMediaDetails]);

  const updatePhotosAndMediaDetails = useCallback(
    (updates: Partial<StaffGetPhotosAndMediaDetailsForCampaignModel>) => {
      setPhotosAndMediaDetails((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          ...updates,
          listingDescription: {
            ...prev.listingDescription,
            ...updates.listingDescription,
          },
          mediaLinks: { ...prev.mediaLinks, ...updates.mediaLinks },
        };
      });
      if (showSavedMessage) setShowSavedMessage(false);
    },
    [showSavedMessage]
  );

  const hasValidationErrors = MediaLinksValidator.hasValidationErrors(validationErrors);
  const isBusy = isPhotosBusy || isFloorPlansBusy;

  const saveChanges = useCallback(() => {
    if (!campaign?.campaignId) {
      updateToastState({
        color: 'error',
        message: 'Campaign ID is required',
        open: true,
      });
      return;
    }

    const allValidationErrors = MediaLinksValidator.validateAllLinks({
      videoLink: photosAndMediaDetails?.mediaLinks?.videoLink,
      virtualTourLink: photosAndMediaDetails?.mediaLinks?.virtualTourLink,
      externalLink: photosAndMediaDetails?.mediaLinks?.externalLink,
    });

    if (MediaLinksValidator.hasValidationErrors(allValidationErrors)) {
      setValidationErrors(allValidationErrors);
      updateToastState({
        color: 'error',
        message: 'Please fix validation errors before saving',
        open: true,
      });
      return;
    }

    staffUpdatePhotosAndMediaDetailsForCampaign({
      campaignId: campaign.campaignId,
      expectedETag: photosAndMediaDetails?.eTag,
      listingDescription: photosAndMediaDetails?.listingDescription,
      mediaLinks: photosAndMediaDetails?.mediaLinks,
      photos: photosAndMediaDetails?.photos,
      floorPlans: photosAndMediaDetails?.floorPlans,
    });
  }, [campaign?.campaignId, photosAndMediaDetails, staffUpdatePhotosAndMediaDetailsForCampaign]);

  useEffect(() => {
    onSaveStateChange({
      saveChanges,
      isFormDirty: isFormDirty && !hasValidationErrors && !isBusy,
      isFormValid: !hasValidationErrors,
      isPending: staffUpdatePhotosAndMediaDetailsForCampaignPending,
      showSavedMessage,
    });
  }, [
    saveChanges,
    isFormDirty,
    hasValidationErrors,
    isBusy,
    showSavedMessage,
    onSaveStateChange,
    staffUpdatePhotosAndMediaDetailsForCampaignPending,
  ]);

  if (!photosAndMediaDetails) return <p role="status">Loading photos and media…</p>;

  return (
    <>
      <AgxToast selector="#agxToast" toastState={toastState} />
      <AgxSpacer direction="vertical" gap="spacing-2xl" padding={{ top: 'spacing-3xl' }} fill>
        <AgxSpacer direction="vertical" fill gap="spacing-3xl">
          {/* Photos */}
          <Card title="Photos" collapsible={false}>
            <AgxSpacer
              direction="vertical"
              id="photosAndMediaPhotosPanel"
              gap="spacing-lg"
              padding={{
                top: 'spacing-3xl',
                bottom: 'spacing-3xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
            >
              <CampaignImageUploader
                campaignId={campaign?.campaignId || ''}
                documentType={UploadedDocumentDocumentType.ListingPropertyImage}
                documents={photosAndMediaDetails?.photos ?? []}
                maxImages={35}
                onDocumentsChange={(updated) => updatePhotosAndMediaDetails({ photos: updated })}
                onBusyStateChange={setIsPhotosBusy}
                id="listing-image-uploader"
                dataTestId="uploader-listing"
              />
            </AgxSpacer>
          </Card>

          {/* Floor Plan */}
          <Card title="Floor Plan" collapsible={false}>
            <AgxSpacer
              direction="vertical"
              id="photosAndMediaFloorPlanPanel"
              gap="spacing-lg"
              padding={{
                top: 'spacing-3xl',
                bottom: 'spacing-3xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
            >
              <CampaignImageUploader
                campaignId={campaign?.campaignId || ''}
                documentType={UploadedDocumentDocumentType.ListingFloorPlan}
                documents={photosAndMediaDetails?.floorPlans ?? []}
                maxImages={2}
                onDocumentsChange={(updated) =>
                  updatePhotosAndMediaDetails({ floorPlans: updated })
                }
                onBusyStateChange={setIsFloorPlansBusy}
                id="floorplan-image-uploader"
                dataTestId="uploader-floorplan"
              />
            </AgxSpacer>
          </Card>

          <Card title="Listing description and media links" collapsible={false}>
            <CampaignPresentationFields
              listingDescription={photosAndMediaDetails.listingDescription}
              mediaLinks={photosAndMediaDetails.mediaLinks}
              onListingDescriptionChange={(listingDescription) =>
                updatePhotosAndMediaDetails({ listingDescription })
              }
              onMediaLinksChange={(mediaLinks) => {
                updatePhotosAndMediaDetails({ mediaLinks });
                setValidationErrors(MediaLinksValidator.validateAllLinks(mediaLinks));
              }}
            />
          </Card>
        </AgxSpacer>
      </AgxSpacer>
    </>
  );
};

export default PhotosAndMedia;
